import { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useNegocio } from '../contexts/NegocioContext';
import axios from 'axios';
import {
  Home, Package, ShoppingCart, Clipboard,
  Users, MapPin, Tag, BarChart2, ClipboardList, DollarSign,
  Bell, PackagePlus, LogOut, Receipt, Layers, Settings, FileText, IdCard, ScrollText, ShieldAlert, BadgeCheck,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VENDEDOR_LINKS = [
  { to: '/mi-jornada',      icon: Home,         label: 'Inicio' },
  { to: '/ver-inventario',  icon: Package,      label: 'Inventario' },
  { to: '/registrar-venta', icon: ShoppingCart, label: 'Ventas' },
  { to: '/corte',           icon: Clipboard,    label: 'Corte' },
];

const REPARTIDOR_LINKS = [
  { to: '/mi-jornada', icon: Home, label: 'Inicio' },
];

const ADMIN_LINKS = [
  { to: '/mi-jornada',                 icon: Home,          label: 'Inicio' },
  { to: '/admin/inventario',           icon: BarChart2,     label: 'Inventario' },
  { to: '/admin/asignaciones',         icon: Package,       label: 'Pollos' },
  { to: '/admin/usuarios',             icon: Users,         label: 'Usuarios' },
  { to: '/admin/puntos',               icon: MapPin,        label: 'Puntos' },
  { to: '/admin/bitacora',             icon: ScrollText,    label: 'Bitácora' },
  { to: '/admin/alertas',              icon: ShieldAlert,   label: 'Alertas' },
  { to: '/admin/autorizaciones',       icon: BadgeCheck,    label: 'Autorizar' },
  { to: '/admin/precios',              icon: Tag,           label: 'Precios' },
  { to: '/admin/listas-precios',       icon: ClipboardList, label: 'Listas' },
  { to: '/admin/categorias-gastos',    icon: Layers,        label: 'Gastos' },
  { to: '/admin/cuentas-por-cobrar',   icon: Receipt,       label: 'Por cobrar' },
  { to: '/admin/clientes',             icon: IdCard,        label: 'Clientes' },
  { to: '/admin/facturacion',          icon: FileText,      label: 'Facturación' },
  { to: '/admin/nomina',               icon: DollarSign,    label: 'Nómina' },
];

const PROPIETARIO_LINKS = [
  { to: '/mi-jornada',                 icon: Home,          label: 'Inicio' },
  { to: '/propietario/dashboard',      icon: BarChart2,     label: 'Dashboard' },
  { to: '/admin/bitacora',             icon: ScrollText,    label: 'Bitácora' },
  { to: '/admin/alertas',              icon: ShieldAlert,   label: 'Alertas' },
  { to: '/admin/autorizaciones',       icon: BadgeCheck,    label: 'Autorizar' },
  { to: '/admin/asignaciones',         icon: Package,       label: 'Pollos' },
  { to: '/admin/inventario',           icon: PackagePlus,   label: 'Inventario' },
  { to: '/admin/cuentas-por-cobrar',   icon: Receipt,       label: 'Por cobrar' },
  { to: '/admin/usuarios',             icon: Users,         label: 'Usuarios' },
  { to: '/admin/puntos',               icon: MapPin,        label: 'Puntos' },
  { to: '/admin/precios',              icon: Tag,           label: 'Precios' },
  { to: '/admin/categorias-gastos',    icon: Layers,        label: 'Gastos' },
  { to: '/admin/clientes',             icon: IdCard,        label: 'Clientes' },
  { to: '/admin/facturacion',          icon: FileText,      label: 'Facturación' },
  { to: '/admin/nomina',               icon: DollarSign,    label: 'Nómina' },
];

export function TopNav() {
  const { user, logout } = useAuth();
  const { nombreNegocio } = useNegocio();
  const navigate = useNavigate();
  const [trasladosCount, setTrasladosCount] = useState(0);
  const [entradasCount, setEntradasCount] = useState(0);
  const [solicitudesCount, setSolicitudesCount] = useState(0);

  useEffect(() => {
    if (!user || user === false) return;

    const fetchPending = async () => {
      try {
        if (user.role === 'repartidor') {
          const { data } = await axios.get(`${API}/repartidor/mis-traslados`);
          setTrasladosCount(data.filter(t => t.status === 'programado').length);
          setEntradasCount(data.filter(t => t.status === 'en_transito').length);
        } else if (user.role === 'admin' || user.role === 'propietario') {
          const requests = [axios.get(`${API}/admin/traslados-dia`)];
          if (user.punto_venta_id) requests.push(axios.get(`${API}/transitos/pendientes/${user.punto_venta_id}`));
          axios.get(`${API}/admin/solicitudes-credito/conteo`)
            .then(({ data: c }) => setSolicitudesCount(c.pendientes || 0)).catch(() => {});
          const [{ data }, transitosRes] = await Promise.all(requests);
          // Traslados: programados (pendientes de salir) + en_transito (en camino) — todos visibles en /admin/inventario
          setTrasladosCount(data.filter(t => t.status === 'programado' || t.status === 'en_transito').length);
          // Entradas: solo tránsitos de producto pendientes de confirmar — visibles en /movimiento?tipo=entrada
          setEntradasCount(transitosRes?.data?.length || 0);
        } else if (user.punto_venta_id) {
          const [{ data: traslados }, { data: transitos }] = await Promise.all([
            axios.get(`${API}/traslados-pos/${user.punto_venta_id}`),
            axios.get(`${API}/transitos/pendientes/${user.punto_venta_id}`),
          ]);
          // salidas = programmed outgoing; entradas = programmed incoming en_transito
          setTrasladosCount((traslados.salidas?.length || 0) + (traslados.entradas?.length || 0));
          // transitos/pendientes = manual salidas from other POS awaiting confirmation
          setEntradasCount(transitos.length || 0);
        }
      } catch { /* silently ignore */ }
    };

    fetchPending();
    const interval = setInterval(fetchPending, 30000);
    return () => clearInterval(interval);
  }, [user?.id, user?.punto_venta_id, user?.role]);

  if (!user || user === false) return null;

  let links = VENDEDOR_LINKS;
  if (user.role === 'repartidor') links = REPARTIDOR_LINKS;
  else if (user.role === 'propietario') links = PROPIETARIO_LINKS;
  else if (user.role === 'admin') links = ADMIN_LINKS;

  const isAdminLike = user.role === 'admin' || user.role === 'propietario';
  const trasladosDest = isAdminLike ? '/admin/inventario' : '/traslados-pos';

  return (
    <nav className="top-nav" data-testid="top-nav">

      {/* Brand name */}
      <div className="flex flex-col items-center justify-center px-3 border-r border-zinc-200 shrink-0">
        <span className="text-xs font-bold text-zinc-900 whitespace-nowrap leading-tight">{nombreNegocio}</span>
      </div>

      {/* Scrollable tabs */}
      <div className="top-nav-tabs">
        {links.map((link) => (
          <NavLink
            key={link.to}
            to={link.to}
            className={({ isActive }) => `top-nav-item${isActive ? ' active' : ''}`}
            data-testid={`nav-${link.label.toLowerCase()}`}
          >
            <span className="relative">
              <link.icon className="w-5 h-5" />
              {link.to === '/admin/autorizaciones' && solicitudesCount > 0 && (
                <span className="absolute -top-1.5 -right-2 min-w-[16px] h-4 px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center"
                  data-testid="badge-autorizaciones">
                  {solicitudesCount}
                </span>
              )}
            </span>
            <span>{link.label}</span>
          </NavLink>
        ))}
      </div>

      {/* Traslados pendientes */}
      <button
        onClick={() => navigate(trasladosDest)}
        className={`top-nav-notif${trasladosCount > 0 ? ' has-pending' : ''}`}
        data-testid="btn-notificaciones-traslados"
        title="Traslados pendientes"
      >
        <span className="top-nav-notif-inner">
          <Bell className="w-5 h-5" />
          <span className={trasladosCount > 0 ? 'notif-badge' : 'notif-badge notif-badge-zero'}>
            {trasladosCount > 99 ? '99+' : trasladosCount}
          </span>
        </span>
        <span>Traslados</span>
      </button>

      {/* Entradas pendientes */}
      <button
        onClick={() => navigate('/movimiento', { state: { tipo: 'entrada' } })}
        className={`top-nav-notif${entradasCount > 0 ? ' has-pending' : ''}`}
        data-testid="btn-notificaciones-entradas"
        title="Entradas pendientes"
      >
        <span className="top-nav-notif-inner">
          <PackagePlus className="w-5 h-5" />
          <span className={entradasCount > 0 ? 'notif-badge' : 'notif-badge notif-badge-zero'}>
            {entradasCount > 99 ? '99+' : entradasCount}
          </span>
        </span>
        <span>Entradas</span>
      </button>

      {/* Configuración */}
      <button
        onClick={() => navigate('/configuracion')}
        className="top-nav-notif"
        title="Configuración"
      >
        <Settings className="w-5 h-5" />
        <span>Config</span>
      </button>

      {/* Cerrar sesión */}
      <button
        onClick={logout}
        className="top-nav-notif"
        data-testid="btn-logout"
        title="Cerrar sesión"
      >
        <LogOut className="w-5 h-5" />
        <span>Salir</span>
      </button>

    </nav>
  );
}
